// Section-rebuild driver for the control pane. Routes UI_PANE_REBUILD_SECTION
// through the shared section registry (./sectionBuilders) so a rebuild rebuilds
// exactly what the boot build built — no duplicated dep wiring. Two keys are
// special: 'data' (the top-bar owns the dataset/field picker, so it's a no-op
// here) and 'selection-focus' (a DOM-only light path that skips a full rebuild).
//
// `which` is typed `SectionRebuildKey`; once 'data'/'selection-focus' are
// handled it has narrowed to BuildableSection, so `buildSection` is called
// without a cast or runtime guard — adding a non-folder key upstream would fail
// to compile here.

import { paneState } from './state';
import { refreshSelectionFocusMarkers } from './panes/selections';
import { buildSection, makeSectionCtx, type SliceDeps } from './sectionBuilders';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { Disposer } from '../types';
import type { ViewerContext } from '@app/context';
import type { ParamsState } from '@store/schema';

export interface SectionDeps extends SliceDeps {
    params: ParamsState;
}

export function installSectionRebuildDriver(viewer: ViewerContext, sectionDeps: SectionDeps): Disposer {
    const ctx = makeSectionCtx(viewer, sectionDeps);

    return store.onIntent(INTENTS.UI_PANE_REBUILD_SECTION, (p) => {
        if (!p || p.which === undefined) return;
        const list = Array.isArray(p.which) ? p.which : [p.which];
        const prevId = p.prevId ?? null;
        const newId = p.newId ?? null;
        for (const which of list) {
            if (which === 'data') continue;
            if (which === 'selection-focus') {
                if (!refreshSelectionFocusMarkers(paneState.refs, prevId, newId)) buildSection(ctx, 'selection');
                continue;
            }
            buildSection(ctx, which);
        }
    });
}
